import React from 'react';
import { AlertTriangle } from 'lucide-react';

const HUD = ({ speed, drinkCount, timeSurvived, distanceTraveled }) => {
  const getWarningLevel = () => {
    if (drinkCount === 0) return 'SAFE';
    if (drinkCount <= 2) return 'CAUTION';
    if (drinkCount <= 4) return 'WARNING';
    return 'DANGER';
  };

  const getWarningColor = () => {
    if (drinkCount === 0) return '#FFFFFF';
    if (drinkCount <= 2) return '#FFD600';
    if (drinkCount <= 4) return '#FF9500';
    return '#FF3333';
  };

  return (
    <div 
      className="absolute bottom-0 left-0 right-0 z-40 no-select"
      data-testid="game-hud"
    >
      {/* DESKTOP VIEW (Original full-width telemetry bar) */}
      <div className="rc-desktop-grid grid-cols-3 border-t border-white/20 bg-black/80 backdrop-blur-md">
        {/* Left: Speed & Distance */}
        <div className="p-4 md:p-6 border-r border-white/10 flex flex-col justify-center">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/50 mb-2">
            TELEMETRY
          </div>
          <div className="font-mono">
            <div className="text-2xl font-bold tracking-widest text-white/80" data-testid="hud-speed">
              {Math.round(speed)} <span className="text-sm text-white/50">KM/H</span>
            </div>
            <div className="text-xl font-bold text-white mt-2" data-testid="hud-distance">
              {distanceTraveled.toFixed(1)}<span className="text-sm text-white/60 ml-1">m</span>
            </div>
          </div>
        </div>

        {/* Center: Warning Level */}
        <div className="p-4 md:p-6 flex flex-col items-center justify-center border-r border-white/10">
          <div className="flex items-center gap-3">
            {drinkCount > 0 && (
              <AlertTriangle className="w-6 h-6" style={{ color: getWarningColor() }} />
            )}
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">
                STATUS
              </div>
              <div 
                className="text-xl font-black tracking-wider"
                style={{ color: getWarningColor(), fontFamily: 'Cabinet Grotesk, sans-serif' }}
                data-testid="hud-warning-level-desktop"
              >
                {getWarningLevel()}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Drinks & Time */}
        <div className="p-4 md:p-6 flex flex-col justify-center">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/50 mb-2">
            IMPAIRMENT
          </div>
          <div className="font-mono">
            <div className="text-2xl font-bold tracking-widest text-white/80" data-testid="hud-drinks">
              {drinkCount} <span className="text-sm text-white/50">DRINKS</span>
            </div>
            <div className="text-sm text-white/60 mt-1" data-testid="hud-time">
              {timeSurvived.toFixed(1)}s
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE VIEW (Floating Card Dashboard) */}
      <div className="rc-mobile-only pb-2.5 px-3">
        <div 
          className="rc-panel grid grid-cols-3 overflow-hidden shadow-2xl max-w-4xl mx-auto"
          style={{
            background: 'rgba(7, 18, 22, 0.88)',
            border: '1px solid var(--rc-border-subtle)',
            borderRadius: '16px',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5), inset 0 0 16px rgba(34, 211, 195, 0.08)'
          }}
        >
          {/* Left: Status / Warning Level */}
          <div className="py-2 px-2 border-r border-teal-500/15 flex flex-col items-center justify-center">
            <div className="flex items-center gap-1.5">
              {drinkCount > 0 && (
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" style={{ color: getWarningColor() }} />
              )}
              <div className="text-center">
                <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-white/50">
                  STATUS
                </div>
                <div 
                  className="text-xs font-black tracking-wider leading-tight"
                  style={{ color: getWarningColor(), fontFamily: 'Cabinet Grotesk, sans-serif' }}
                  data-testid="hud-warning-level"
                >
                  {getWarningLevel()}
                </div>
              </div>
            </div>
          </div>

          {/* Center (MIDDLE): Speedometer & Distance */}
          <div className="py-2 px-2 border-r border-teal-500/15 flex flex-col items-center justify-center bg-teal-500/5">
            <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-teal-300/70 mb-0.5">
              SPEED & DISTANCE
            </div>
            <div className="font-mono flex items-baseline gap-1.5">
              <div className="text-base font-bold tracking-widest text-white" data-testid="hud-speed-mobile">
                {Math.round(speed)} <span className="text-[10px] text-teal-400 font-semibold">KM/H</span>
              </div>
              <div className="text-xs font-semibold text-white/80" data-testid="hud-distance-mobile">
                {distanceTraveled.toFixed(1)}<span className="text-[9px] text-white/50 ml-0.5">m</span>
              </div>
            </div>
          </div>

          {/* Right: Drinks & Time */}
          <div className="py-2 px-2 flex flex-col items-center justify-center">
            <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-white/50 mb-0.5">
              IMPAIRMENT
            </div>
            <div className="font-mono flex items-baseline gap-1.5">
              <div className="text-xs font-bold tracking-widest text-white/90" data-testid="hud-drinks-mobile">
                {drinkCount} <span className="text-[9px] text-white/50">DRINKS</span>
              </div>
              <div className="text-[10px] text-white/60" data-testid="hud-time-mobile">
                {timeSurvived.toFixed(1)}s
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HUD;
